/**
 * API Client for Public Booking
 *
 * Provides typed request wrappers for consuming the public booking API.
 * Handles production URL routing (Railway backend) vs development proxy.
 */

// Default Railway production URL
const DEFAULT_API_URL = "https://rutalink-production.up.railway.app";

/**
 * Custom error class for API errors
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly data?: unknown
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Helper to construct full API URL
 * In production: returns full Railway URL (from env var or default)
 * In development: returns relative URL (Vite proxy handles it)
 */
export function getApiUrl(endpoint: string): string {
  const cleanEndpoint = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;

  // In production, use VITE_API_URL or default Railway URL
  if (import.meta.env.PROD) {
    const baseUrl = import.meta.env.VITE_API_URL || DEFAULT_API_URL;
    return `${baseUrl}${cleanEndpoint}`;
  }

  // In development, use relative URLs (Vite proxy handles routing to localhost:5000)
  return cleanEndpoint;
}

// Exported for use elsewhere (e.g., WebSocket connection)
export const API_URL = import.meta.env.PROD
  ? import.meta.env.VITE_API_URL || DEFAULT_API_URL
  : "";

/**
 * Default headers for all API requests
 */
const defaultHeaders: HeadersInit = {
  "Content-Type": "application/json",
};

/**
 * Generic response handler that throws ApiError on non-OK responses
 */
async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}`;
    let errorData: unknown;

    try {
      errorData = await response.json();
      if (
        typeof errorData === "object" &&
        errorData !== null &&
        "message" in errorData
      ) {
        errorMessage = (errorData as { message: string }).message;
      }
    } catch {
      // Response body wasn't JSON, use status text
      errorMessage = response.statusText || errorMessage;
    }

    throw new ApiError(response.status, errorMessage, errorData);
  }

  // Handle empty responses (204 No Content)
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

/**
 * Public API client with typed request methods
 */
export const publicApi = {
  /**
   * GET request wrapper
   */
  async get<T>(endpoint: string): Promise<T> {
    const response = await fetch(getApiUrl(endpoint), {
      method: "GET",
      headers: defaultHeaders,
      credentials: "include",
    });
    return handleResponse<T>(response);
  },

  /**
   * POST request wrapper
   */
  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    const response = await fetch(getApiUrl(endpoint), {
      method: "POST",
      headers: defaultHeaders,
      credentials: "include",
      body: data ? JSON.stringify(data) : undefined,
    });
    return handleResponse<T>(response);
  },

  /**
   * DELETE request wrapper
   */
  async delete<T>(endpoint: string): Promise<T> {
    const response = await fetch(getApiUrl(endpoint), {
      method: "DELETE",
      headers: defaultHeaders,
      credentials: "include",
    });
    return handleResponse<T>(response);
  },

  /**
   * PUT request wrapper (for completeness)
   */
  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    const response = await fetch(getApiUrl(endpoint), {
      method: "PUT",
      headers: defaultHeaders,
      credentials: "include",
      body: data ? JSON.stringify(data) : undefined,
    });
    return handleResponse<T>(response);
  },

  /**
   * PATCH request wrapper (for completeness)
   */
  async patch<T>(endpoint: string, data?: unknown): Promise<T> {
    const response = await fetch(getApiUrl(endpoint), {
      method: "PATCH",
      headers: defaultHeaders,
      credentials: "include",
      body: data ? JSON.stringify(data) : undefined,
    });
    return handleResponse<T>(response);
  },
};

export default publicApi;
